import { AlertTriangle } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

/**
 * TINA error boundaries — two flavors per CODING-STANDARDS.md §3.
 *
 *   <RootErrorBoundary>      catastrophic failure — full-page error
 *   <RouteErrorBoundary>     per-route — keeps AppShell mounted, in-page error
 *
 * Both report to console; production hookup to Sentry happens via the
 * sentry-react `ErrorBoundary` wrap in main.tsx (Sprint 13+).
 */
interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  fallback: (error: Error, reset: () => void) => ReactNode;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console -- canonical error reporting path
    console.error("[TINA error boundary]", error, errorInfo);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return this.props.children;
  }
}

function RootFallback({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page p-6">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-danger-subtle text-danger">
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
        </div>
        <h1 className="m-0 text-2xl font-bold text-slate-900">
          {t("common.something_went_wrong")}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{t("common.weve_notified")}</p>
        {import.meta.env.DEV && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-surface-alt p-3 text-left text-xs text-slate-700">
            {error.message}
            {"\n"}
            {error.stack}
          </pre>
        )}
        <div className="mt-6 flex justify-center gap-2">
          <Button onClick={() => window.location.reload()}>
            {t("common.refresh")}
          </Button>
          <Button variant="secondary" onClick={reset}>
            {t("common.try_again")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RouteFallback({ reset }: { reset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-danger/30 bg-danger-subtle p-6 text-center">
      <AlertTriangle className="mx-auto h-6 w-6 text-danger" aria-hidden="true" />
      <h2 className="m-0 mt-3 text-lg font-semibold text-slate-900">
        {t("common.page_failed")}
      </h2>
      <p className="m-0 mt-1 text-sm text-slate-600">{t("common.weve_logged")}</p>
      <div className="mt-4 flex justify-center gap-2">
        <Button size="sm" onClick={reset}>
          {t("common.retry")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => (window.location.href = "/dashboard")}
        >
          {t("common.back_to_dashboard")}
        </Button>
      </div>
    </div>
  );
}

export function RootErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(error, reset) => <RootFallback error={error} reset={reset} />}
    >
      {children}
    </ErrorBoundary>
  );
}

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary fallback={(_error, reset) => <RouteFallback reset={reset} />}>
      {children}
    </ErrorBoundary>
  );
}
